// [PHASE B / #013 — STRING-SIN SWEEP / RULE 11]: Single source of truth
//   for weapon-class detection. Pre-extraction the same `POLEARM_NAMES`
//   array was duplicated literally in src/services/effectCalculator.js
//   and src/data/srdFeats.js (the Polearm Master feature) — meaning a
//   future addition to the polearm list (e.g., a CW-authored polearm
//   variant) would silently skip the Polearm Master AC trigger because
//   only one of the two lists got updated.
//
// Per Rule 22 the test imports from here, the production callers import
//   from here. Both eyes see the same canonical list.

/**
 * Canonical lowercase polearm baseType / name tokens.
 *
 * Detection cascade in `isPolearm()`:
 *   1. Explicit `weapon.weaponClass === 'polearm'` (preferred — set by
 *      future BASE_ITEMS rows or CW custom weapons).
 *   2. `weapon.baseType` exact match against this set (covers SRD
 *      polearms whose BASE_ITEMS rows lack `weaponClass`).
 *   3. Substring match on baseType / name (legacy — covers magic
 *      polearms like `glaive_plus_1` and homebrew display names).
 */
export const POLEARM_BASE_TYPES = Object.freeze(new Set([
  'quarterstaff',
  'spear',
  'glaive',
  'halberd',
  'pike',
  'lance',
]));

/**
 * @param {object|null|undefined} weapon - InventoryItem-shaped weapon
 *   (or any object with .weaponClass, .baseType, .name).
 * @returns {boolean}
 */
export function isPolearm(weapon) {
  if (!weapon || typeof weapon !== 'object') return false;
  if (weapon.weaponClass === 'polearm') return true;
  const base = typeof weapon.baseType === 'string' ? weapon.baseType.toLowerCase() : '';
  if (base && POLEARM_BASE_TYPES.has(base)) return true;
  const name = typeof weapon.name === 'string' ? weapon.name.toLowerCase() : '';
  for (const token of POLEARM_BASE_TYPES) {
    if ((base && base.includes(token)) || (name && name.includes(token))) {
      return true;
    }
  }
  return false;
}
